import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { MaterialUICore, Heading } from '@iclinic/design-system';

import { image } from 'shared/utils/urlGetter';
import {
  Modal,
  ModalContainer,
  FlexContainer,
  CardContainer,
  Body,
  Logo,
  ShapeUp,
  ShapeDown,
} from './SelfOnboardingModal.styles';
import Card from './components/Card';
import {
  disableOnboardingFeatureToogle,
  getTestPacientId,
} from './state/services';
import { getUserInfo } from './state/selectors';
import { eventTracking } from './utils';
import SUBSCRIPTION_PERMISSIONS from 'shared/constants/subscriptionPermissions';
import { captureException } from 'shared/utils/handlerErrors';

const { Fade } = MaterialUICore;

export const REMOVE_ONBOARDINGMODAL_PERMISSION_URL =
  '/usuarios/remove-session-onboarding-permission/?next=';

const OnBoardingModal = () => {
  const [showModal, setShowModal] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const {
    physicianId,
    profileId,
    subscriptionId,
    isSubscriber,
    isAdmin,
    permissions,
  } = useSelector(getUserInfo);

  const hasFinances = permissions?.includes(SUBSCRIPTION_PERMISSIONS.Finances);

  const getRecordsURL = async () => {
    let url = '/pacientes/';
    try {
      const response = await getTestPacientId();

      if (response && response.data) {
        url = `/prontuarios/${response.data.patient_id}/`;
      }
    } catch (error) {
      captureException(error);
    }

    return url;
  };

  const moveToPage = async (
    url: string,
    buttonName: string,
    clickEvent: React.MouseEvent<HTMLElement>,
  ) => {
    clickEvent.stopPropagation();

    if (!physicianId) {
      return;
    }

    try {
      await disableOnboardingFeatureToogle(physicianId);
      eventTracking(
        buttonName,
        profileId || '',
        subscriptionId || '',
        !!isSubscriber,
      );

      setShowModal(false);
      window.location.href = `${REMOVE_ONBOARDINGMODAL_PERMISSION_URL}${url}`;
    } catch (error) {
      captureException(error);
      setShowModal(false);
    }
  };

  const onClickSchedule = (clickEvent: React.MouseEvent<HTMLElement>) =>
    moveToPage('/agenda/', 'self_onboarding_calendar', clickEvent);

  const onClickRecords = async (clickEvent: React.MouseEvent<HTMLElement>) => {
    clickEvent.stopPropagation();
    setIsLoading(true);

    const url = await getRecordsURL();
    await moveToPage(url, 'self_onboarding_medical_record', clickEvent);

    setIsLoading(false);
  };

  const onClickFinance = (clickEvent: React.MouseEvent<HTMLElement>) =>
    moveToPage('/financas/', 'self_onboarding_finances', clickEvent);

  return (
    <Modal open={showModal}>
      <Fade in>
        <ModalContainer>
          <ShapeUp src={image('onBoarding.shapeUp')} />
          <ShapeDown src={image('onBoarding.shapeDown')} />
          <FlexContainer>
            <Heading variant="xxl" align="center" inverseTextColor>
              Bem-vindo ao iClinic
            </Heading>
            <Body bold variant="sm" align="center" inverseTextColor>
              Clique no que você gostaria de conhecer primeiro
            </Body>
            <CardContainer>
              <Card
                title="Prontuário"
                text="Seu parceiro nos atendimentos"
                imagePath={image('onBoarding.records')}
                imageAlt="Prontuário"
                dataGa="self_onboarding_medical_record"
                onClickEvent={onClickRecords}
                isLoading={isLoading}
              />
              <Card
                title="Agenda"
                text="Sua organização começa aqui"
                imagePath={image('onBoarding.schedule')}
                imageAlt="Agenda"
                dataGa="self_onboarding_calendar"
                onClickEvent={onClickSchedule}
              />
              {isAdmin && hasFinances && (
                <Card
                  title="Financeiro"
                  text="Sua gestão sob controle"
                  imagePath={image('onBoarding.finances')}
                  imageAlt="Financeiro"
                  dataGa="self_onboarding_finances"
                  onClickEvent={onClickFinance}
                />
              )}
            </CardContainer>
            <Logo src={image('onBoarding.logo')} alt="iClinic logo" />
          </FlexContainer>
        </ModalContainer>
      </Fade>
    </Modal>
  );
};

export default OnBoardingModal;
